import React, { useState, forwardRef, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, Modal, ScrollView, Alert } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_ACTION } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import apis from '@api/api';
import actionList from '@navigation/screens/MidLevel/DataScreen/ActionHandler/Components/utils/actionList';
import { YStack, XStack, SizableText, Button, H2, Separator } from 'tamagui';
import { FontAwesome5 } from '@expo/vector-icons';
import GenerateSchemaForm from '@hooks/generateSchemaForm';

interface ActionDialogProps {
  visible: boolean;
  actionType: 'Create' | 'Read' | 'Update' | 'Delete';
  onClose: () => void;
  onSave?: (actionData: any) => void;
  dataId?: string; // The data schema ID to save actions to
}

const ActionDialog: React.FC<ActionDialogProps> = ({ visible, actionType, onClose, onSave, dataId }) => {
  console.log('🎯 ActionDialog render:', { visible, actionType, dataId });
  const dispatch = useDispatch();
  const schemaFormRef = useRef<any>(null);

  // Get current actions from Redux store
  const currentActions = useSelector((state: DefaultRootState) => state.actionMaker.present.actions || []);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    actionKey: 'email', // email, whatsapp, cal, project, etc.
    when: 'immediate', // immediate, scheduled
    scheduledTime: '',
    // Schema form data
    schemaData: {},
  });

  // Get the schema for the selected action type
  const getActionSchema = () => {
    const action = actionList.find(a => a.key === formData.actionKey);
    if (!action || !action.component) {
      return null;
    }

    // Return schema based on action type
    switch (formData.actionKey) {
      case 'email':
        return {
          'stage_action': { type: 'template_select', ref: 'template_select' },
          'headline': { type: "string" },
          'when': {
            type: 'dropDown',
            content: { enum: ['immediately', 'after', 'before', 'on'] }
          },
        };
      case 'whatsapp':
        return {
          'stage_action': { type: 'template_select', ref: 'template_select' },
          'headline': { type: "string" },
          'when': {
            type: 'dropDown',
            content: { enum: ['immediately', 'after', 'before', 'on'] }
          },
        };
      case 'calendar':
        return {
          'stage_action': { type: 'calendar_select', ref: 'calendar_select' },
          'headline': { type: "string" },
          'when': {
            type: 'dropDown',
            content: { enum: ['immediately', 'after', 'before', 'on'] }
          },
        };
      case 'project':
        return {
          'stage_action': { type: 'template_select', ref: 'template_select' },
          'headline': { type: "string" },
          'when': {
            type: 'dropDown',
            content: { enum: ['immediately', 'after', 'before', 'on'] }
          },
        };
      default:
        return {
          'headline': { type: "string" },
          'when': {
            type: 'dropDown',
            content: { enum: ['immediately', 'after', 'before', 'on'] }
          },
        };
    }
  };

  const handleSave = async () => {
    try {
      // Get schema form data if available
      let schemaFormData = {};
      if (schemaFormRef.current?.getFormData) {
        schemaFormData = await schemaFormRef.current.getFormData();
      }

      // Create action data structure matching backend expectations
      const actionData = {
        id: Date.now().toString(),
        key: formData.actionKey, // email, whatsapp, cal
        actionType: actionType.toLowerCase(), // create, read, update, delete
        when: formData.when, // immediate, scheduled
        scheduledTime: formData.when === 'scheduled' ? formData.scheduledTime : undefined,
        name: formData.name || `${actionType} ${formData.actionKey} Action`,
        description: formData.description,
        // Action-specific configuration from schema form
        config: {
          ...schemaFormData,
          // Override with basic form data if schema form is not available
          ...(Object.keys(schemaFormData).length === 0 && {
            headline: formData.name,
            when: formData.when
          })
        },
        // UI metadata for the flow designer
        position: { x: Math.random() * 200, y: Math.random() * 200 },
        createdAt: new Date().toISOString(),
        component: null,
      };

      console.log('🎯 Action data created:', actionData);

      // Add to Redux store for UI updates
      dispatch({ type: ADD_ACTION, payload: { actionData } });
      console.log('✅ Action added to Redux state:', actionData);

      // Show success message
      Alert.alert('Success', 'Action added successfully!');

      // Call optional onSave callback with the action data
      if (onSave) {
        onSave(actionData);
      }

      // Reset form and close
      setFormData({
        name: '',
        description: '',
        actionKey: 'email',
        when: 'immediate',
        scheduledTime: '',
        schemaData: {},
      });
      onClose();
    } catch (error) {
      console.error('❌ Error saving action:', error);
      Alert.alert('Error', 'Failed to save action. Please try again.');
    }
  };

  const getActionTypeColor = () => {
    switch (actionType) {
      case 'Create': return '#10B981'; // Green
      case 'Read': return '#3B82F6';   // Blue
      case 'Update': return '#F59E0B'; // Orange
      case 'Delete': return '#EF4444'; // Red
      default: return '#6B7280';       // grey
    }
  };

  const getActionTypeDescription = () => {
    switch (actionType) {
      case 'Create': return 'Create new data or resources';
      case 'Read': return 'Retrieve and display data';
      case 'Update': return 'Modify existing data';
      case 'Delete': return 'Remove data or resources';
      default: return 'Configure action';
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={{ flex: 1, backgroundColor: '#f8f9fa' }}>
        {/* Header */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 20,
          paddingTop: 60,
          backgroundColor: 'white',
          borderBottomWidth: 1,
          borderBottomColor: '#e5e7eb',
        }}>
          <View>
            <Text style={{
              fontSize: 24,
              fontWeight: 'bold',
              color: getActionTypeColor(),
            }}>
              {actionType} Action
            </Text>
            <Text style={{
              fontSize: 14,
              color: '#6b7280',
              marginTop: 4,
            }}>
              {getActionTypeDescription()}
            </Text>
          </View>
          <TouchableOpacity
            onPress={onClose}
            style={{
              padding: 8,
              borderRadius: 8,
              backgroundColor: '#f3f4f6',
            }}
          >
            <Text style={{ fontSize: 16, color: '#6b7280' }}>✕</Text>
          </TouchableOpacity>
        </View>

        {/* Form */}
        <ScrollView style={{ flex: 1, padding: 20 }}>
          {/* Action Name */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
              Action Name
            </Text>
            <TextInput
              value={formData.name}
              onChangeText={(text) => setFormData({ ...formData, name: text })}
              placeholder={`${actionType} Action`}
              style={{
                borderWidth: 1,
                borderColor: '#d1d5db',
                borderRadius: 8,
                padding: 12,
                backgroundColor: 'white',
                fontSize: 16,
              }}
            />
          </View>

          {/* Description */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
              Description
            </Text>
            <TextInput
              value={formData.description}
              onChangeText={(text) => setFormData({ ...formData, description: text })}
              placeholder="Describe what this action does..."
              multiline
              numberOfLines={3}
              style={{
                borderWidth: 1,
                borderColor: '#d1d5db',
                borderRadius: 8,
                padding: 12,
                backgroundColor: 'white',
                fontSize: 16,
                height: 80,
                textAlignVertical: 'top',
              }}
            />
          </View>

          {/* Action Type Selection */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
              Action Type
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              {actionList.map((action) => {
                const colors = {
                  email: '#3B82F6',
                  whatsapp: '#10B981',
                  calendar: '#F59E0B',
                  project: '#8B5CF6',
                  call: '#EF4444'
                };
                const color = colors[action.key] || '#6B7280';

                return (
                  <TouchableOpacity
                    key={action.key}
                    onPress={() => setFormData({ ...formData, actionKey: action.key })}
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      borderRadius: 8,
                      backgroundColor: formData.actionKey === action.key ? color : '#f3f4f6',
                      borderWidth: 2,
                      borderColor: formData.actionKey === action.key ? color : '#d1d5db',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <FontAwesome5
                      name={action.icon}
                      size={16}
                      color={formData.actionKey === action.key ? 'white' : '#6b7280'}
                    />
                    <Text style={{
                      color: formData.actionKey === action.key ? 'white' : '#6b7280',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      {action.title}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Timing Selection */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
              When to Execute
            </Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {[
                { key: 'immediate', label: '⚡ Immediate', color: '#EF4444' },
                { key: 'scheduled', label: '⏰ Scheduled', color: '#8B5CF6' }
              ].map((timing) => (
                <TouchableOpacity
                  key={timing.key}
                  onPress={() => setFormData({ ...formData, when: timing.key })}
                  style={{
                    flex: 1,
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    borderRadius: 8,
                    backgroundColor: formData.when === timing.key ? timing.color : '#f3f4f6',
                    borderWidth: 2,
                    borderColor: formData.when === timing.key ? timing.color : '#d1d5db',
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: formData.when === timing.key ? 'white' : '#6b7280',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    {timing.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Scheduled Time (if scheduled) */}
          {formData.when === 'scheduled' && (
            <View style={{ marginBottom: 20 }}>
              <Text style={{ fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                Scheduled Time
              </Text>
              <TextInput
                value={formData.scheduledTime}
                onChangeText={(text) => setFormData({ ...formData, scheduledTime: text })}
                placeholder="2024-01-15T10:00:00Z"
                style={{
                  borderWidth: 1,
                  borderColor: '#d1d5db',
                  borderRadius: 8,
                  padding: 12,
                  backgroundColor: 'white',
                  fontSize: 16,
                }}
              />
            </View>
          )}

          {/* Action-Specific Configuration */}
          <View style={{
            backgroundColor: 'white',
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            borderWidth: 1,
            borderColor: '#e5e7eb',
          }}>
            <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 16, color: '#374151' }}>
              {formData.actionKey === 'email' ? '📧 Email Configuration' :
                formData.actionKey === 'whatsapp' ? '💬 WhatsApp Configuration' :
                  formData.actionKey === 'calendar' ? '📅 Calendar Configuration' :
                    formData.actionKey === 'project' ? '📋 Project Configuration' :
                      formData.actionKey === 'call' ? '📞 Call Configuration' :
                        '⚙️ Action Configuration'}
            </Text>

            {/* GenerateSchemaForm for dynamic field types */}
            {getActionSchema() && (
              <GenerateSchemaForm
                ref={schemaFormRef}
                buttonLabel='Save'
                scrollEnabled
                customStyling={{ minHeight: 300, paddingRight: 10 }}
                initialData={formData.schemaData}
                schema={getActionSchema()}
                hideButton
                submitFunction={(vals) => {
                  console.log('🎯 Schema form submitted:', vals);
                  setFormData({ ...formData, schemaData: vals });
                  return Promise.resolve(vals);
                }}
              />
            )}

            {/* Fallback for actions without schema */}
            {!getActionSchema() && (
              <View style={{ padding: 20, alignItems: 'center' }}>
                <Text style={{ color: '#6b7280', fontSize: 16 }}>
                  No specific configuration required for this action type.
                </Text>
              </View>
            )}

          </View>
        </ScrollView>

        {/* Footer */}
        <View style={{
          flexDirection: 'row',
          gap: 12,
          padding: 20,
          paddingBottom: 40,
          backgroundColor: 'white',
          borderTopWidth: 1,
          borderTopColor: '#e5e7eb',
        }}>
          <TouchableOpacity
            onPress={onClose}
            style={{
              flex: 1,
              padding: 16,
              borderRadius: 8,
              backgroundColor: '#f3f4f6',
              alignItems: 'center',
            }}
          >
            <Text style={{ fontSize: 16, fontWeight: '600', color: '#6b7280' }}>
              Cancel
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={handleSave}
            style={{
              flex: 2,
              padding: 16,
              borderRadius: 8,
              backgroundColor: getActionTypeColor(),
              alignItems: 'center',
            }}
          >
            <Text style={{ fontSize: 16, fontWeight: '600', color: 'white' }}>
              Add {formData.actionKey === 'email' ? '📧 Email' :
                formData.actionKey === 'whatsapp' ? '💬 WhatsApp' :
                  '📅 Calendar'} Action
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default ActionDialog;
